/**
 * Siapa yang mengajukan sebuah koreksi — MURNI, tanpa React.
 *
 * ADA KARENA KEPUTUSAN DIAMBIL BUTA. Kartu antrean koreksi menampilkan jenis izin, tanggal, dan
 * alasannya, tetapi TIDAK menyebut satu pun nama. Manajer menekan "Setujui" untuk pengajuan sakit
 * tanpa tahu siapa yang sakit — dan pada organisasi dengan lebih dari satu orang, itu bukan
 * ketidaknyamanan melainkan persetujuan yang tidak dapat dipertanggungjawabkan.
 *
 * Servernya sendiri hanya mengirim UUID (`subjectUserId`, `requestedByUserId`); nama hidup di
 * `GET /api/members`. Pemetaannya karena itu terjadi di sini, bukan di server — dan itu pilihan
 * sadar: mengubah bentuk respons koreksi menuntut `fly deploy` manual, sementara nama yang hilang
 * dari layar adalah hal yang menagih hari ini.
 */

import type { AttendanceCorrection } from '../api/types';
import { namaLayak } from './display-name';
import type { Member } from './worker-candidates';

/**
 * Peta `userId` → nama tampil. Anggota tanpa nama yang BERARTI sengaja TIDAK dimasukkan — termasuk
 * yang `displayName`-nya alamat email (`namaLayak`, lihat `display-name.ts`), supaya jatuh ke
 * cadangan JUJUR `namaAnggota()` di bawah, bukan tampil sebagai email di kartu persetujuan.
 */
export function petaNamaAnggota(members: readonly Member[]): ReadonlyMap<string, string> {
  const peta = new Map<string, string>();
  for (const m of members) {
    const nama = namaLayak(m.displayName);
    if (nama) peta.set(m.userId, nama);
  }
  return peta;
}

/**
 * Nama seseorang untuk ditampilkan, dengan cadangan yang JUJUR.
 *
 * Yang tidak ditemukan tidak dikosongkan dan tidak ditulis "-". Keduanya membuat dua orang
 * berbeda tampak sebagai baris yang sama, dan manajer yang menyetujui dua pengajuan kosong tidak
 * punya cara mengetahui bahwa itu dua orang. Potongan id-nya disertakan justru supaya keduanya
 * dapat dibedakan — dan supaya jelas bahwa yang hilang adalah NAMANYA, bukan orangnya.
 *
 * Sebab paling wajar nama tidak ketemu: orangnya sudah dikeluarkan dari organisasi, sementara
 * pengajuan lamanya tetap ada di antrean.
 */
export function namaAnggota(peta: ReadonlyMap<string, string>, userId: string): string {
  return peta.get(userId) ?? `Tidak dikenal · ${userId.slice(0, 8)}`;
}

export interface PengajuKoreksi {
  /** Orang yang koreksinya BERLAKU untuk dirinya — yang sakit, yang terlambat. */
  readonly subjek: string;
  /**
   * Terisi HANYA bila yang mengajukan bukan orangnya sendiri.
   *
   * Manajer boleh mengajukan atas nama anak buahnya. Bila itu terjadi, menyembunyikannya membuat
   * kartu berbohong tentang siapa yang mengetik pengajuannya; bila tidak terjadi, menampilkan
   * "diajukan oleh <nama yang sama>" hanya menambah satu baris tanpa keterangan baru.
   */
  readonly diajukanOleh: string | null;
}

export function pengajuKoreksi(
  peta: ReadonlyMap<string, string>,
  koreksi: Pick<AttendanceCorrection, 'subjectUserId' | 'requestedByUserId'>,
): PengajuKoreksi {
  return {
    subjek: namaAnggota(peta, koreksi.subjectUserId),
    diajukanOleh:
      koreksi.requestedByUserId === koreksi.subjectUserId
        ? null
        : namaAnggota(peta, koreksi.requestedByUserId),
  };
}
